import { useCallback, useEffect, useState } from "react";
import { History, Tag } from "lucide-react";
import { toast } from "sonner";
import { crmService } from "@/services/crmService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const card = "rounded-md border border-[#22252F] bg-[#12141A] p-4";

/** Cartão de status comercial no perfil existente do cliente (não substitui nada). */
export const ClientStatusCard = ({ clientId, profile, onChanged }) => {
  const meta = profile?.crm_meta;
  const [selected, setSelected] = useState(profile?.crm_status || "new_lead");

  useEffect(() => { setSelected(profile?.crm_status || "new_lead"); }, [profile?.crm_status]);

  const allowed = meta?.allowed || [];
  const options = (meta?.statuses || []).filter((s) => allowed.includes(s.slug));

  const save = async (e) => {
    e.preventDefault();
    try {
      await crmService.setStatus(clientId, selected);
      toast.success("Status comercial atualizado");
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className={`${card} space-y-4`} data-testid="client-status-card">
      <div className="flex flex-wrap items-center gap-2">
        <Tag className="h-4 w-4 text-[#60A5FA]" />
        <span className="text-sm text-white">Status comercial</span>
        <StatusBadge slug={profile?.crm_status} label={profile?.crm_status_label}
                     testId="profile-crm-status" />
        {(profile?.derived_badges || []).map((b, i) => (
          <StatusBadge key={b} slug={b} label={(profile?.derived_labels || [])[i]} />
        ))}
      </div>

      {profile?.crm_status_updated_at ? (
        <p data-testid="crm-status-meta" className="text-xs text-[#94A3B8]">
          Alterado por {profile.crm_status_updated_by || "—"} em {fmtDate(profile.crm_status_updated_at)}
        </p>
      ) : null}

      <form onSubmit={save} className="flex flex-wrap gap-2">
        <select data-testid="crm-status-select" value={selected} onChange={(e) => setSelected(e.target.value)}
                className="h-10 flex-1 min-w-[12rem] rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
          {options.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
        </select>
        <Button type="submit" data-testid="submit-crm-status"
                className="h-10 rounded-md bg-[#2563EB] text-xs uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
          Aplicar status
        </Button>
      </form>
    </div>
  );
};

/** Histórico de status — bloco próprio, com altura limitada e scroll. */
export const ClientStatusHistory = ({ clientId, refreshKey = 0 }) => {
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      setHistory(await crmService.statusHistory(clientId));
    } catch {
      setHistory([]);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className={card} data-testid="crm-status-history">
      <p className="mb-2 flex items-center gap-2 text-sm text-white">
        <History className="h-4 w-4 text-[#60A5FA]" /> Histórico de status
      </p>
      <div className="max-h-64 overflow-y-auto pr-1">
        {history.length === 0 ? (
          <p className="py-4 text-center text-sm text-[#64748B]">Sem alterações registadas</p>
        ) : history.map((h) => (
          <div key={h.id} className="border-b border-[#1A1D25] py-2 text-xs last:border-0">
            <p className="text-[#94A3B8]">{h.from_label} → <span className="text-white">{h.to_label}</span></p>
            <div className="mt-0.5 flex items-center justify-between gap-2">
              <span className="text-[#94A3B8]">{h.author_name} · <span className="font-mono uppercase text-[#60A5FA]">{h.author_role}</span></span>
              <span className="font-mono text-[#64748B]">{fmtDate(h.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
